import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Empty, Loading } from '../../components/Ui';
import { currency } from '../../utils/format';

/**
 * The 360° walk-through of a showroom: one panorama per area, hotspots a
 * customer clicks to see the product standing at that spot. The photo is drawn
 * wider than the frame and dragged sideways, so it reads as looking around.
 */
const ShowroomTourPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState(null);
  const frame = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    api.partners.detail(id)
      .then((r) => setPartner(r.data.partner || r.data))
      .catch(() => setPartner(null))
      .finally(() => setLoading(false));
  }, [id]);

  const sections = [...((partner && partner.tour) || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const section = sections[index];

  // Open each area looking straight ahead, not at its left edge.
  useEffect(() => {
    setSpot(null);
    const el = frame.current;
    if (el) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, [index, partner]);

  const startDrag = (e) => {
    drag.current = { x: e.clientX, left: frame.current.scrollLeft };
  };
  const onDrag = (e) => {
    if (!drag.current) return;
    frame.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
  };
  const endDrag = () => { drag.current = null; };

  if (loading) return <Loading text="Opening the showroom…" />;
  if (!section) {
    return (
      <Empty
        emoji="🎥"
        title="No tour yet"
        text="This showroom has not uploaded its 360° photos. Call them or visit to see the stock."
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="360° showroom"
        subtitle={`${partner.businessName} · ${section.title || `Area ${index + 1}`}`}
        back
      />

      <div
        className="pano-frame"
        ref={frame}
        onPointerDown={startDrag}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        <div className="pano-inner">
          <img src={fileUrl(section.image)} alt={section.title || 'Showroom'} draggable={false} />
          {(section.hotspots || []).map((h, i) => (
            <button
              type="button"
              key={`${h.x}-${h.y}-${i}`}
              className="pano-spot"
              style={{ left: `${h.x}%`, top: `${h.y}%` }}
              onClick={() => setSpot(h)}
              title={h.label || 'Product'}
            >
              🛒
            </button>
          ))}
        </div>
      </div>
      <div className="t-tiny" style={{ textAlign: 'center', marginTop: 6 }}>
        Drag to look around · click a 🛒 to see the product
      </div>

      <div className="row wrap" style={{ gap: 8, margin: '14px 0' }}>
        {sections.map((s, i) => (
          <Chip key={`${s.title}-${i}`} label={s.title || `Area ${i + 1}`} active={i === index} onClick={() => setIndex(i)} />
        ))}
      </div>

      {spot ? (
        <div className="card sh-md">
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            {spot.product && (spot.product.images || []).length ? (
              <img
                src={fileUrl(spot.product.images[0])}
                alt={spot.product.title}
                style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover' }}
              />
            ) : null}
            <div className="grow">
              <div className="t-title">{(spot.product && spot.product.title) || spot.label || 'Product'}</div>
              {spot.product ? (
                <>
                  <div className="t-body" style={{ color: 'var(--success)', fontWeight: 800 }}>{currency(spot.product.price)}</div>
                  {spot.product.warranty ? <div className="t-tiny">🛡️ {spot.product.warranty}</div> : null}
                  <div className="t-tiny">{spot.product.inStock ? 'Available' : 'Out of stock'}</div>
                </>
              ) : (
                <div className="t-tiny">{spot.note || 'Ask the showroom about this.'}</div>
              )}
            </div>
            <button type="button" className="link-btn" onClick={() => setSpot(null)}>✕</button>
          </div>
          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            {spot.product ? (
              <Button title="View details" variant="ghost" onClick={() => navigate(`/products/${spot.product._id}`)} />
            ) : null}
            <Button title="Enquire" onClick={() => navigate(`/business/${partner._id}`)} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShowroomTourPage;
